import { Switch } from '@material-ui/core';
import usePreferences from 'hooks/usePreferences';
import React, { useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Dialog, Divider, List, Text } from 'react-native-paper';
import { METRICS, MetricType } from 'shared/components/metrics';
import { LiveOrder, LiveViewMode, TeamDisplayMode } from 'utils/types';
import MetricTypesField from './MetricTypesField';
import { sortBy } from 'lodash';
import OptionsPicker from 'shared/components/OptionsPicker';
import { t } from 'shared/i18n';
import { Workout } from 'shared/types';

interface Props {
  onDismiss: () => void;
  workout?: Workout;
}

const AVAILABLE_METRICS = Object.values(MetricType);

const LiveSettings = ({ onDismiss, workout }: Props) => {
  const {
    liveMetricTypes,
    setLiveMetricTypes,
    showLiveIntervalMetrics,
    setShowLiveIntervalMetrics,
    liveOrder,
    liveViewMode,
    setLiveOrder,
    showLiveReactions,
    toggleLiveReactions,
    showAlwaysFullName,
    toggleShowAlwaysFullName,
    teamDisplayMode,
    setTeamDisplayMode,
  } = usePreferences();

  const isComp = workout?.track?.compStatus != null;

  const availableMetrics = useMemo(() => {
    return sortBy(AVAILABLE_METRICS, (metric) => METRICS[metric].getName());
  }, []);

  return (
    <div style={{ position: 'fixed', width: '100%', height: '100%' }}>
      <Dialog visible onDismiss={onDismiss} style={styles.dialog}>
        <Dialog.Title>{t('Metrics')}</Dialog.Title>

        <ScrollView>
          {liveViewMode === LiveViewMode.Tiles && (
            <List.Item
              title={
                <Text>
                  {t('Display current metrics')}:{' '}
                  <Text style={{ fontWeight: 'bold' }}>
                    {showLiveIntervalMetrics ? t('off') : t('on')}
                  </Text>
                </Text>
              }
              description={t('When off it displays interval metrics')}
              right={() => (
                <Switch
                  checked={!showLiveIntervalMetrics}
                  onChange={() => {
                    setShowLiveIntervalMetrics(!showLiveIntervalMetrics);
                  }}
                />
              )}
            />
          )}
          <List.Item
            title={
              <Text>
                {t('Race mode')}:{' '}
                <Text style={{ fontWeight: 'bold' }}>
                  {liveOrder !== LiveOrder.Join ? t('on') : t('off')}
                </Text>
              </Text>
            }
            right={() => (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <OptionsPicker
                  options={[
                    { value: LiveOrder.Join, name: t('Off') },
                    { value: LiveOrder.Pace, name: t('By Overall Avg Pace') },
                    {
                      value: LiveOrder.IntervalPace,
                      name: t('By Interval Avg Pace'),
                    },
                    {
                      value: LiveOrder.Progress,
                      name: t('By Overall Distance'),
                    },
                    {
                      value: LiveOrder.IntervalProgress,
                      name: t('By Interval Distance'),
                    },
                  ]}
                  currentValue={liveOrder}
                  onChange={(newOrder) => {
                    setLiveOrder(newOrder);
                  }}
                />
              </View>
            )}
          />
          <List.Item
            title={
              <Text>
                {t('Live reactions')}:{' '}
                <Text style={{ fontWeight: 'bold' }}>
                  {showLiveReactions ? t('on') : t('off')}
                </Text>
              </Text>
            }
            right={() => (
              <Switch
                checked={showLiveReactions}
                onChange={() => {
                  toggleLiveReactions();
                }}
              />
            )}
          />
          <List.Item
            title={
              <Text>
                {t('Always Full Name')}:{' '}
                <Text style={{ fontWeight: 'bold' }}>
                  {showAlwaysFullName ? t('on') : t('off')}
                </Text>
              </Text>
            }
            right={() => (
              <Switch
                checked={showAlwaysFullName}
                onChange={() => {
                  toggleShowAlwaysFullName();
                }}
              />
            )}
          />
          {isComp &&
            (liveViewMode === LiveViewMode.Tiles ||
              liveViewMode === LiveViewMode.Rows) && (
              <List.Item
                title={<Text>{t('Display mode')}: </Text>}
                right={() => (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <OptionsPicker
                      options={[
                        {
                          value: TeamDisplayMode.OnlyAthletes,
                          name: t('Only Athletes'),
                        },
                        {
                          value: TeamDisplayMode.OnlyTeams,
                          name: t('Only Teams'),
                        },
                        {
                          value: TeamDisplayMode.AthletesAndTeams,
                          name: t('Athletes & Teams'),
                        },
                        {
                          value: TeamDisplayMode.TeamsAndAthletesWithoutTeams,
                          name: t('Teams & Athletes without teams'),
                        },
                      ]}
                      currentValue={teamDisplayMode}
                      onChange={(newMode) => {
                        setTeamDisplayMode(newMode);
                      }}
                    />
                  </View>
                )}
              />
            )}
          <MetricTypesField
            availableMetrics={availableMetrics}
            metricTypes={liveMetricTypes}
            setMetricTypes={setLiveMetricTypes}
          />
        </ScrollView>

        <Divider />
        <Dialog.Actions>
          <Button onPress={onDismiss}>{t('Close')}</Button>
        </Dialog.Actions>
      </Dialog>
    </div>
  );
};

const styles = StyleSheet.create({
  dialog: {
    height: '90vh',
    alignSelf: 'center',
    width: '90%',
    maxWidth: 400,
  },
});

export default LiveSettings;
